import { loadBundledPack } from '@dl/shared';

import { CalendarSchema, type Calendar } from './types.js';

/**
 * Bundled-pack calendar loader.
 *
 * The `core` pack ships two calendars as JSON data (tickets II.C.2 +
 * II.C.3): `gregorian` and `harptos`. The Zod parse here is the second
 * layer of validation — the pack-loader has already verified the pack's
 * top-level shape; this step verifies that each calendar entry conforms
 * to `CalendarSchema`.
 *
 * The CI invariant "no client/server divergence" hinges on this single
 * entry point: both sides of the wire import `loadBundledCalendars()`
 * from `@dl/calendar`. Any third-party pack loaded in later phases will
 * pipe through the same `CalendarSchema.parse` chokepoint.
 */

export const GREGORIAN_CALENDAR_ID = 'gregorian';
export const HARPTOS_CALENDAR_ID = 'harptos';

/** Ordered list of calendar ids shipped by the `official-core` pack. */
export const CORE_CALENDAR_IDS = [GREGORIAN_CALENDAR_ID, HARPTOS_CALENDAR_ID] as const;
export type CoreCalendarId = (typeof CORE_CALENDAR_IDS)[number];

/**
 * The bundled pack that ships the engine's starter calendars. core.json is
 * gone (ADR-0004 §3, #183); the former core calendars (`gregorian`, `harptos`)
 * now ride on the official starter pack. The pack `calendars[]` entries keep
 * their token ids (`gregorian` / `harptos`) — those are campaign-pointer
 * handles validated by `CalendarSchema`, distinct from the `cal_` minted ids
 * the `pack.define_calendar` meta-op uses.
 */
export const BUNDLED_CALENDAR_PACK_ID = 'official-core';

/**
 * Load every calendar declared by a bundled pack. Validates each entry
 * through `CalendarSchema`. Throws if any entry is malformed (a pack
 * that ships an invalid calendar fails the engine boot fast — calendars
 * are not optional infrastructure once a campaign selects one).
 */
export function loadBundledCalendars(packId = BUNDLED_CALENDAR_PACK_ID): Calendar[] {
  const pack = loadBundledPack(packId);
  const raw = pack.calendars ?? [];
  return raw.map((entry, index) => {
    const parsed = CalendarSchema.safeParse(entry);
    if (!parsed.success) {
      const issues = parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ');
      throw new Error(`pack '${packId}' calendar[${index}] is malformed: ${issues}`);
    }
    return parsed.data;
  });
}

/**
 * Load a single calendar by id from a bundled pack. Throws if the id is
 * not declared by the pack (callers can fall back to a default — e.g.
 * the client defaults to `gregorian` until a Campaign sets its own
 * `canonical_calendar`).
 */
export function loadBundledCalendar(calendarId: string, packId = BUNDLED_CALENDAR_PACK_ID): Calendar {
  const calendars = loadBundledCalendars(packId);
  const found = calendars.find((c) => c.id === calendarId);
  if (found === undefined) {
    throw new Error(`pack '${packId}' has no calendar '${calendarId}'`);
  }
  return found;
}
